import type {
  WatermarkConfig,
  WatermarkInstance,
  WatermarkOptions
} from '@/types/watermark';

/**
 * 水印工具类
 * 特性：
 * 1. 使用Canvas绘制水印，转换为base64背景图
 * 2. 缓存机制避免重复生成相同水印
 * 3. MutationObserver监听DOM变化实现防篡改
 * 4. pointer-events: none 防止文本选中
 */
class WatermarkManager {
  private static instance: WatermarkManager;
  private watermarkCache = new Map<string, string>();
  private observer: MutationObserver | null = null;
  private watermarkElement: HTMLElement | null = null;
  private currentOptions: WatermarkConfig | null = null;

  private constructor() {}

  static getInstance(): WatermarkManager {
    if (!WatermarkManager.instance) {
      WatermarkManager.instance = new WatermarkManager();
    }
    return WatermarkManager.instance;
  }

  /**
   * 生成水印图片的缓存key
   */
  private getCacheKey(options: WatermarkOptions): string {
    const {
      text,
      fontSize,
      fontColor,
      fontFamily,
      width,
      height,
      rotate,
      letterSpacing,
      scale,
      lineHeight,
      textAlign
    } = options;
    const textStr = Array.isArray(text) ? text.join('|') : text;
    return `${textStr}_${fontSize}_${fontColor}_${fontFamily}_${width}_${height}_${rotate}_${letterSpacing}_${scale}_${lineHeight}_${textAlign}`;
  }

  /**
   * 生成水印背景图片
   */
  private generateWatermarkImage(options: WatermarkOptions): string {
    const {
      text,
      fontSize = 16,
      fontColor = 'rgba(0,0,0,0.15)',
      fontFamily = 'Arial',
      width = 120,
      height = 64,
      rotate = -20,
      letterSpacing = 0,
      scale = 1,
      lineHeight = fontSize * 1.2,
      textAlign = 'center'
    } = options;

    // 标准化文本为数组格式
    const textLines = Array.isArray(text) ? text : [text];

    const cacheKey = this.getCacheKey(options);

    // 检查缓存
    if (this.watermarkCache.has(cacheKey)) {
      return this.watermarkCache.get(cacheKey)!;
    }

    // 创建Canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    // 超高清渲染，专门针对小字体优化
    const devicePixelRatio = window.devicePixelRatio || 1;
    // 对于极小字体，使用更高的渲染倍数以获得超清晰效果
    const renderScale =
      fontSize <= 4
        ? Math.max(devicePixelRatio * 3, 6) // 极小字体：6倍以上
        : fontSize <= 6
          ? Math.max(devicePixelRatio * 2.5, 5) // 超小字体：5倍以上
          : fontSize <= 8
            ? Math.max(devicePixelRatio * 2, 4) // 小字体：4倍以上
            : fontSize <= 12
              ? Math.max(devicePixelRatio * 1.5, 3)
              : Math.min(devicePixelRatio, 2);

    const actualWidth = width * renderScale;
    const actualHeight = height * renderScale;

    canvas.width = actualWidth;
    canvas.height = actualHeight;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    // 应用高倍数缩放
    ctx.scale(renderScale, renderScale);

    // 超高质量渲染设置
    ctx.imageSmoothingEnabled = false; // 对于小字体，关闭平滑以获得锐利边缘
    if ('imageSmoothingQuality' in ctx) {
      ctx.imageSmoothingQuality = 'high';
    }

    // 针对极小字体的特殊处理
    let adjustedFontSize = fontSize;
    if (fontSize <= 4) {
      // 极小字体：大幅放大并优化
      adjustedFontSize = Math.max(fontSize * 2.5, 10);
    } else if (fontSize <= 6) {
      // 超小字体：强制放大并优化
      adjustedFontSize = Math.max(fontSize * 2, 9);
    } else if (fontSize <= 8) {
      // 小字体：适度放大
      adjustedFontSize = fontSize * 1.5;
    } else if (fontSize <= 12) {
      // 正常小字体：轻微放大
      adjustedFontSize = fontSize * 1.2;
    }

    // 设置优化的字体样式
    ctx.font = `${adjustedFontSize}px ${fontFamily}`;
    ctx.fillStyle = fontColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // 清除阴影设置，避免模糊
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // 针对极小字体使用增强描边技术
    if (fontSize <= 4) {
      // 极小字体：粗描边
      ctx.lineWidth = 0.4;
      ctx.strokeStyle = fontColor;
    } else if (fontSize <= 6) {
      // 超小字体：中等描边
      ctx.lineWidth = 0.3;
      ctx.strokeStyle = fontColor;
    } else if (fontSize <= 8) {
      // 小字体：细描边
      ctx.lineWidth = 0.2;
      ctx.strokeStyle = fontColor;
    }

    // 旋转并绘制文字
    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.rotate((Math.PI / 180) * rotate);
    // 应用用户设置的缩放
    ctx.scale(scale, scale);

    // 绘制多行文本
    this.drawMultiLineText(
      ctx,
      textLines,
      0,
      0,
      letterSpacing,
      fontSize,
      lineHeight,
      textAlign
    );

    ctx.restore();

    // 转换为base64并缓存
    const dataURL = canvas.toDataURL('image/png');
    this.watermarkCache.set(cacheKey, dataURL);

    return dataURL;
  }

  /**
   * 绘制多行文本
   */
  private drawMultiLineText(
    ctx: CanvasRenderingContext2D,
    textLines: string[],
    x: number,
    y: number,
    letterSpacing: number,
    fontSize: number,
    lineHeight: number,
    textAlign: 'left' | 'center' | 'right'
  ): void {
    if (!textLines || textLines.length === 0) {
      return;
    }

    // 计算总高度
    const totalHeight = (textLines.length - 1) * lineHeight;

    // 计算起始Y位置（垂直居中）
    let currentY = y - totalHeight / 2;

    // 绘制每一行
    textLines.forEach(line => {
      if (line && line.length > 0) {
        // 根据对齐方式设置文本对齐
        ctx.textAlign = textAlign;

        if (letterSpacing > 0) {
          // 有字符间距时，使用特殊绘制方法
          this.drawTextWithLetterSpacing(
            ctx,
            line,
            x,
            currentY,
            letterSpacing,
            fontSize
          );
        } else {
          // 确保像素对齐以获得清晰的渲染效果
          const drawX = Math.round(x);
          const drawY = Math.round(currentY);

          // 极小字体使用描边+填充的双重渲染技术
          if (fontSize <= 6) {
            ctx.strokeText(line, drawX, drawY); // 先描边增强边缘
            ctx.fillText(line, drawX, drawY); // 再填充保证可读性
          } else {
            ctx.fillText(line, drawX, drawY);
          }
        }
      }

      currentY += lineHeight;
    });
  }

  /**
   * 绘制带字符间距的文字
   */
  private drawTextWithLetterSpacing(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    letterSpacing: number,
    fontSize: number
  ): void {
    if (!text || text.length === 0) {
      return;
    }

    // 计算总文字宽度（包含字符间距）
    const characters = Array.from(text);
    let totalWidth = 0;

    // 计算每个字符的宽度
    const charWidths = characters.map(char => {
      return ctx.measureText(char).width;
    });

    // 计算总宽度
    totalWidth =
      charWidths.reduce((sum, width) => {
        return sum + width;
      }, 0) +
      (characters.length - 1) * letterSpacing;

    // 计算起始位置（居中对齐）
    let currentX = x - totalWidth / 2;

    // 逐个绘制字符，确保像素对齐
    characters.forEach((char, index) => {
      const charX = Math.round(currentX + charWidths[index] / 2);
      const charY = Math.round(y);

      // 超小字体使用描边+填充技术
      if (fontSize <= 8) {
        ctx.strokeText(char, charX, charY);
        ctx.fillText(char, charX, charY);
      } else {
        ctx.fillText(char, charX, charY);
      }

      currentX += charWidths[index] + letterSpacing;
    });
  }

  /**
   * 创建水印DOM元素
   */
  private createWatermarkElement(options: WatermarkConfig): HTMLElement {
    const {
      zIndex = 9999,
      gap = 100,
      offset = [0, 0],
      container,
      fullscreen = true
    } = options;

    const watermarkImage = this.generateWatermarkImage(options);

    const element = document.createElement('div');
    element.className = 'watermark-layer';

    // 设置样式
    Object.assign(element.style, {
      position: fullscreen ? 'fixed' : 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      zIndex: zIndex.toString(),
      pointerEvents: 'none', // 防止阻挡页面交互和文本选择
      backgroundImage: `url(${watermarkImage})`,
      backgroundRepeat: 'repeat',
      backgroundPosition: `${offset[0]}px ${offset[1]}px`,
      backgroundSize: `${gap}px`,
      userSelect: 'none', // 防止选中水印文字
      webkitUserSelect: 'none',
      mozUserSelect: 'none',
      msUserSelect: 'none'
    });

    // 添加到容器
    const targetContainer = container || document.body;
    targetContainer.appendChild(element);

    return element;
  }

  /**
   * 启动防篡改监听
   */
  private startMonitoring(
    element: HTMLElement,
    options: WatermarkConfig
  ): void {
    if (!options.monitor) {
      return;
    }

    this.observer = new MutationObserver(mutations => {
      let shouldRerender = false;

      mutations.forEach(mutation => {
        // 检查水印元素是否被删除
        if (mutation.type === 'childList') {
          mutation.removedNodes.forEach(node => {
            if (node === element) {
              shouldRerender = true;
            }
          });
        }

        // 检查样式是否被修改
        if (
          mutation.type === 'attributes' &&
          mutation.target === element &&
          mutation.attributeName === 'style'
        ) {
          shouldRerender = true;
        }
      });

      if (shouldRerender) {
        // 触发监听回调
        options.onMonitor?.();

        // 重新渲染水印
        setTimeout(() => {
          this.render();
        }, 0);
      }
    });

    // 监听整个文档的变化
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }

  /**
   * 创建水印实例
   */
  create(options: WatermarkConfig): WatermarkInstance {
    this.currentOptions = {
      fontSize: 16,
      fontColor: 'rgba(0,0,0,0.15)',
      fontFamily: 'Arial',
      width: 120,
      height: 64,
      rotate: -20,
      zIndex: 9999,
      gap: 100,
      offset: [0, 0],
      letterSpacing: 0,
      scale: 1,
      monitor: true,
      fullscreen: true,
      ...options
    };

    this.render();

    return {
      update: (newOptions: Partial<WatermarkOptions>) => {
        if (this.currentOptions) {
          Object.assign(this.currentOptions, newOptions);
          this.render();
        }
      },
      destroy: () => {
        this.destroy();
      },
      render: () => {
        this.render();
      }
    };
  }

  /**
   * 渲染水印
   */
  private render(): void {
    if (!this.currentOptions) {
      return;
    }

    // 清除现有水印
    this.clearWatermark();

    // 创建新水印
    this.watermarkElement = this.createWatermarkElement(this.currentOptions);

    // 启动监听
    this.startMonitoring(this.watermarkElement, this.currentOptions);
  }

  /**
   * 清除水印
   */
  private clearWatermark(): void {
    if (this.watermarkElement && this.watermarkElement.parentNode) {
      this.watermarkElement.parentNode.removeChild(this.watermarkElement);
    }
    this.watermarkElement = null;

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  /**
   * 销毁水印
   */
  destroy(): void {
    this.clearWatermark();
    this.currentOptions = null;
  }

  /**
   * 清除缓存
   */
  clearCache(): void {
    this.watermarkCache.clear();
  }
}

// 导出单例和工厂函数
export const watermarkManager = WatermarkManager.getInstance();

/**
 * 创建水印实例
 * @param options 水印配置选项
 * @returns 水印实例
 */
export const createWatermark = (
  options: WatermarkConfig
): WatermarkInstance => {
  return watermarkManager.create(options);
};

/**
 * 快速创建全屏水印
 * @param text 水印文字，支持字符串或字符串数组（多行）
 * @param options 其他配置选项
 * @returns 水印实例
 */
export const createFullScreenWatermark = (
  text: string | string[],
  options: Partial<Omit<WatermarkOptions, 'text'>> = {}
): WatermarkInstance => {
  return createWatermark({
    ...options,
    text,
    fullscreen: true
  });
};
